import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, Minimize2, Maximize2 } from 'lucide-react';
import axios from 'axios';
import { getAuthHeader } from '../../lib/auth';

const Chatbot = ({ isDraggable = false }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL; 
  const API_ENDPOINT = `${BASE_URL}/api/chatbot`;

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: '👋 Hello! I\'m your travel assistant. How can I help you today?',
      time: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0 });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, isOpen, isMinimized]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (isMinimized) {
      setIsMinimized(false);
    }
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      time: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Removed the setTimeout to make the call real-time
    const botResponse = await getBotResponse(inputMessage);
    
    setIsTyping(false);
    setMessages((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        type: 'bot',
        content: botResponse || "Sorry, I couldn't reach the server.",
        time: new Date(),
      },
    ]);
  };

  const getBotResponse = async (query) => {
    try {
      const res = await axios.post(API_ENDPOINT, { message: query }, {
        headers: {
          Authorization: getAuthHeader()
        }
      });

      const data = res.data;

      if (data?.reply) {
        let text = data.reply;

        text = text.replace(/\*\*(.*?)\*\*/g, "$1");
        text = text.replace(/\*(.*?)\*/g, "$1");

        return text;
      }
      return "I didn't get a valid response.";
    } catch (error) {
      console.error("Chatbot API failed:", error);
      return "Sorry, something went wrong with the connection.";
    }
  };

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const handleMouseDown = (e) => {
    if (!isDraggable || !isOpen) return;
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX - position.x,
      startY: e.clientY - position.y
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isDraggable) return;
    setPosition({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div 
      className={`fixed z-[9999] transition-all duration-300 ease-in-out
        ${isOpen 
          ? 'inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:w-[400px] sm:h-[650px] sm:max-h-[90vh]' 
          : 'bottom-4 right-4 w-14 h-14 sm:w-16 sm:h-16'}
        ${isMinimized ? 'h-16 w-60 sm:w-72' : ''}
        ${isDragging ? 'cursor-move' : ''}
      `}
      style={isDraggable && isOpen ? {
        transform: `translate(${position.x}px, ${position.y}px)`,
        transition: isDragging ? 'none' : 'all 0.3s ease-in-out'
      } : {}}
    >
      {/* Chat Toggle Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center hover:scale-110"
          aria-label="Open chat"
        >
          <MessageSquare className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="w-full h-full bg-white rounded-none sm:rounded-2xl shadow-2xl flex flex-col">
          {/* Header */}
          <div 
            className={`p-3 sm:p-4 bg-gradient-to-r from-teal-600 to-teal-700 rounded-none sm:rounded-t-2xl shadow-md flex items-center justify-between ${isDraggable ? 'cursor-move' : ''}`}
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-medium text-base sm:text-lg">Travel Assistant</h3>
                <p className="text-white/90 text-xs sm:text-sm">Always here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="text-white/80 hover:text-white p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                {isMinimized ? <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
              <button
                onClick={closeChat}
                className="text-white/80 hover:text-white p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Close chat"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[80%] p-2.5 sm:p-3 rounded-2xl shadow-md ${
                        message.type === 'user'
                          ? 'bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-br-none'
                          : 'bg-gray-100 text-gray-800 rounded-bl-none'
                      }`}
                    >
                      {/* FIX 3: Added whitespace-pre-wrap to handle \n newlines from backend */}
                      <p className="text-sm break-words whitespace-pre-wrap">{message.content}</p>
                      <p className="text-xs mt-1 opacity-80">{formatTime(message.time)}</p>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-2xl p-3 shadow-md">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-teal-600 rounded-full animate-pulse"></div>
                        <div className="w-2 h-2 bg-teal-600 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-teal-600 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 sm:p-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 text-sm sm:text-base rounded-xl border border-gray-200 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20"
                  />
                  <button
                    onClick={handleSendMessage}
                    className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl p-2.5 sm:p-3 hover:from-teal-700 hover:to-teal-800 transition-all"
                    aria-label="Send message"
                  >
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Chatbot;
